import { useContext, useMemo } from 'react';
import { AnalyticsContext, getLocalStorage } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useMount } from '@shared/utils/hooks/useMount';

const AUTO_POPULATED_PLACES = (eventId?: string | null) => `autoPopulatedAccommodationPlaces_${eventId}`;

/**
 * @deprecated Use useAutoPopulation instead
 *
 * Hook to determine whether hotel suggestions are auto-populated for the current event.
 *
 * ### Logic:
 * - Checks localStorage for a flag tied to the current event ID.
 * - Uses a feature flag (`isAutoPopulationDone`) to determine user-level opt-in.
 * - Skips feature flag evaluation if event info is still loading or localStorage already confirms auto-population.
 * - Tracks usage of the legacy localStorage flag for analytics.
 * - Provides a setter to set auto population done manually.
 *
 * @returns An object containing:
 * - `isAutoPopulationDone`: Whether hotels are auto-populated (from localStorage or feature flag).
 * - `setIsAutoPopulationDone`: Callback to set auto population done.
 * - `isLoadingAutoPopulationFlag`: Whether the state is still loading.
 */
export const useIsAutoPopulationDone = () => {
  const localStorage = getLocalStorage();
  const analytics = useContext(AnalyticsContext);
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();

  const [isAutoPopulatedFromConsentKey, loadingIsAutoPopulatedFromConsentKey, setAutoPopulationDoneConsentKey] = useIsFeatureEnabled({
    consentKey: 'isAutoPopulationDone',
  });

  const isAutoPopulatedFromStorage = useMemo(() => {
    const localStorageKey = eventInfo?.eventId ? AUTO_POPULATED_PLACES(eventInfo.eventId) : null;
    return localStorageKey ? localStorage.getItem(localStorageKey) === 'true' : false;
  }, [eventInfo?.eventId, localStorage]);

  const isLoadingAutoPopulationFlag = loadingEventInfo || loadingIsAutoPopulatedFromConsentKey;

  const setIsAutoPopulationDone = useEventCallback(() => {
    // Cleanup existing localstorage feature flag, so we could early switch to useIsFeatureEnabled.
    if (isAutoPopulatedFromStorage) {
      localStorage.setItem(AUTO_POPULATED_PLACES(eventInfo?.eventId), null);
    }
    if (!isAutoPopulatedFromConsentKey) {
      setAutoPopulationDoneConsentKey(true);
    }
  });

  /**
   * Tracks usage of the legacy localStorage flag for auto-populated hotels.
   * This is temporary and can be removed once the feature flag fully replaces localStorage logic.
   */
  useMount(() => {
    if (isAutoPopulatedFromStorage) {
      analytics.track({
        action: 'TravelButtonInteracted',
        category: 'wedding',
        pagePrefix: 'admin',
        extraInfo: {
          name: 'autoPopulatedFromStorage',
          isAutoPopulatedFromStorage,
        },
      });
    }
  });

  const isAutoPopulationDone = isAutoPopulatedFromConsentKey || isAutoPopulatedFromStorage;

  return {
    isAutoPopulationDone,
    setIsAutoPopulationDone,
    isLoadingAutoPopulationFlag,
  };
};
